import classNames from 'classnames';
import React, { useState } from 'react';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import Button, { IconButton } from '../components/Button';
import Caret from '../components/Caret';
import closeIcon from '../icons/non-prefilled/16x16/clear.svg';
import Link from '../utils/router/Link';
import useMainContentHistory from '../utils/router/useMainContentHistory';
import * as styles from './SettingsContainerView.module.scss';
import SettingsSidebar from './SettingsSidebar';

interface SettingsContainerViewProps {
  className?: string;
  title: React.ReactNode;
  titleRight?: React.ReactNode;
  parentTitle?: string;
  parentLink?: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}

const SettingsContainerView = ({
  className,
  title,
  titleRight,
  parentTitle,
  parentLink,
  subtitle,
  children,
}: SettingsContainerViewProps) => {
  const [isMenuActive, setMenuActive] = useState(false);
  const mainContentHistory = useMainContentHistory();

  const onClose = () => {
    mainContentHistory.back();
  };

  const header = (
    <>
      Settings: <span className={styles.title}>{parentTitle || title}</span>
      <div className={styles.menuCaret}>
        <Caret direction={isMenuActive ? 'up' : 'down'} />
      </div>
    </>
  );

  return (
    <div
      className={classNames(className, styles.container, {
        [styles.menuActive]: isMenuActive,
      })}
    >
      <div className={styles.topbar}>
        <TwoColumnRow
          full={
            <div className={styles.topbarContent}>
              <span className={styles.topbarText}>{header}</span>
              <Button
                className={styles.topbarButton}
                onClick={() => setMenuActive(!isMenuActive)}
              >
                {header}
              </Button>
              <div className={styles.closeButton}>
                <IconButton
                  onClick={onClose}
                  applyNegativeMargin
                  aria-label="Close"
                >
                  {closeIcon}
                </IconButton>
              </div>
            </div>
          }
        />
      </div>
      <div className={styles.content}>
        <TwoColumnRow
          main={(isSidebarVisible) => (
            <>
              {isMenuActive && !isSidebarVisible && (
                <div className={styles.navigationFloating}>
                  <SettingsSidebar />
                </div>
              )}
              <div
                className={classNames(styles.settings, {
                  [styles.isSidebarVisible]: isSidebarVisible,
                })}
              >
                <header>
                  {parentTitle && parentLink && (
                    <div className={styles.breadcrumb}>
                      <Link to={parentLink}>{parentTitle}</Link>
                      <span className={styles.caret}>
                        <Caret inline direction="right" size={6} />
                      </span>
                    </div>
                  )}
                  {typeof title === 'string' ? (
                    <div className={styles.settingsTitle}>
                      <h2>{title}</h2>
                      {titleRight}
                    </div>
                  ) : (
                    title
                  )}
                  {!!subtitle && (
                    <div className={styles.subtitle}>{subtitle}</div>
                  )}
                </header>
                {children}
              </div>
            </>
          )}
          aside={
            <div className={styles.navigationAside}>
              <SettingsSidebar />
            </div>
          }
          asideLocation="left"
        />
      </div>
    </div>
  );
};

export default SettingsContainerView;
